"use client";

import { ChevronLeft } from "lucide-react";
import { useMemo, useState } from "react";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { feelingsCategories } from "@/lib/feelings-data";

type Feeling = {
    emoji: string;
    name: string;
};

type FeelingsModalProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSelect: (feeling: Feeling) => void;
};

type Category = keyof typeof feelingsCategories;

export function FeelingsModal({ open, onOpenChange, onSelect }: FeelingsModalProps) {
    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
    const [searchQuery, setSearchQuery] = useState("");

    const filteredFeelings = useMemo(() => {
        if (!selectedCategory)
            return [];
        const categoryFeelings = feelingsCategories[selectedCategory] || [];
        return categoryFeelings.filter(feeling =>
            feeling.name.toLowerCase().includes(searchQuery.toLowerCase()),
        );
    }, [selectedCategory, searchQuery]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogTitle className=" sr-only">Feeling Modal</DialogTitle>
            <DialogContent className="max-w-[340px] md:max-w-lg">
                {!selectedCategory
                    ? (
                        <>
                            <DialogHeader>
                                <DialogTitle className="text-xl font-bold">How are you feeling?</DialogTitle>
                            </DialogHeader>
                            <div className="grid grid-cols-2 gap-4">
                                {Object.keys(feelingsCategories).map(category => (
                                    <button
                                      key={category}
                                      onClick={() => setSelectedCategory(category as Category)}
                                      className="flex cursor-pointer items-center gap-2 p-3 rounded-lg hover:bg-accent"
                                    >
                                        <span className="text-2xl">
                                            {feelingsCategories[category as Category][0].emoji}
                                        </span>
                                        <span>{category}</span>
                                    </button>
                                ))}
                            </div>
                        </>
                    )
                    : (
                        <>
                            <div className="flex items-center gap-4 mb-4">
                                <button
                                  onClick={() => setSelectedCategory(null)}
                                  className="p-2 cursor-pointer rounded-full hover:bg-accent"
                                >
                                    <ChevronLeft className="h-5 w-5" />
                                </button>
                                <h2 className="text-xl font-bold">{selectedCategory}</h2>
                            </div>
                            <Input
                              type="text"
                              placeholder="Search feelings..."
                              value={searchQuery}
                              onChange={e => setSearchQuery(e.target.value)}
                              className="w-full p-2 mb-4 border rounded-lg"
                            />
                            <div className="grid grid-cols-2 gap-2 max-h-[300px] overflow-y-auto">
                                {filteredFeelings.map(feeling => (
                                    <button
                                      key={feeling.name}
                                      onClick={() => onSelect(feeling)}
                                      className="flex cursor-pointer items-center gap-2 p-2 rounded-lg hover:bg-accent"
                                    >
                                        <span className="text-xl">{feeling.emoji}</span>
                                        <span>{feeling.name}</span>
                                    </button>
                                ))}
                            </div>
                        </>
                    )}
            </DialogContent>
        </Dialog>
    );
}
